/**
 * WGSL for the network passes: instanced sphere impostors, instanced screen-space line ribbons, and
 * instanced volume impostors for the group regions.
 *
 * All three are billboards rather than geometry, which is the decision the whole renderer rests on.
 * A
 * tessellated sphere is a few hundred triangles per node and a tube is a few dozen per edge
 * segment, so an estate of a thousand components with three thousand references costs a million
 * triangles before anything is on screen. A quad with a shaded normal costs two, looks identical at
 * the sizes a node is ever drawn at, and turns the whole frame into a handful of draw calls.
 *
 * The region pass is the one that is not a stand-in for geometry it could have drawn. A group is a
 * volume, and integrating a density along the ray through it is what the thing *is* rather than an
 * approximation of a mesh - which is why it is the one fragment here that does not call
 * `toLitColor`. There is no surface to light.
 *
 * The lighting follows `getShadingTerms` and `applyFog` in structure - two lights, Blinn-Phong,
 * aerial perspective - but no longer in arithmetic, and that is the one deliberate divergence in
 * this file. It works in **linear radiance**: instance colors arrive decoded, the rig arrives
 * through `toLinearLighting`, the specular goes into the diffuse term's remaining headroom rather
 * than being added to it and clamped, and the target is an `-srgb` view so the hardware encodes on
 * store and blends in linear.
 *
 * `SURFACE_SHADER` still multiplies into encoded color. Summing two lights, adding a specular and
 * mixing toward fog are all linear operations, so doing them on encoded values is the wrong curve
 * rather than a small error - it is why a specular there reads as a decal stuck on the surface. The
 * surface chart has not moved because its two renderers have to agree with each other and its GPU
 * path reads color from a ramp texture that would have to be decoded too; until that is done, a
 * difference between these two files is this note rather than a bug.
 *
 * The uniform struct is the contract with `packNetworkUniforms`, field for field and in order.
 * Every member is `vec4` or larger, for the reason that file gives: a `vec3` aligns to 16 bytes and
 * sizes as 12, and the resulting drift reads the wrong numbers rather than failing.
 */
import { NETWORK_MARKER_GLYPHS } from '../../networkMarkers';

export const NETWORK_SHADER = /* wgsl */ `
const MINIMUM_DIVISOR : f32 = 0.1;

/**
 * Everything in this shader is linear radiance: the instance colors arrive decoded, the rig's
 * weights arrive as linear weights, and the color target is an \`-srgb\` view, so the hardware
 * encodes on write and blends in linear. Which leaves the specular as the only term that can
 * overflow, and this is what receives it - into the headroom the diffuse left, asymptotically, so a
 * highlight keeps shape inside it where a clamp would flatten it to one white patch.
 *
 * Per channel via \`exp\` on the vector, which is componentwise in WGSL. The headroom is floored
 * before the divide: at a diffuse of exactly 1 there is nothing to add into, and the floor makes
 * that term vanish rather than divide by zero.
 */
fn addHighlight(diffuse : vec3<f32>, highlight : vec3<f32>) -> vec3<f32> {
  let headroom = max(vec3<f32>(0.0), vec3<f32>(1.0) - diffuse);
  let falloff = vec3<f32>(1.0) - exp(-highlight / max(headroom, vec3<f32>(1e-4)));
  return min(vec3<f32>(1.0), diffuse + headroom * falloff);
}

struct Uniforms {
  viewProjection : mat4x4<f32>,
  basisRight     : vec4<f32>,
  basisUp        : vec4<f32>,
  basisForward   : vec4<f32>,
  // xyz world-space direction, w diffuse
  lightDirection : array<vec4<f32>, 2>,
  // rgb color, w specular
  lightColor     : array<vec4<f32>, 2>,
  // rgb fog color, w strength
  fog            : vec4<f32>,
  // ambient, shininess, depthExtent, unused
  params         : vec4<f32>,
  // device-pixel width, device-pixel height, pixel ratio, unused
  viewport       : vec4<f32>,
  // rgb surface color, w unused
  surface        : vec4<f32>,
  // travelling phase in device pixels, pulse strength, lead strength, unused
  flow           : vec4<f32>,
  // strength, halo (0 or 1), sampling radius in device pixels, depth range in world units
  occlusion      : vec4<f32>,
};

@group(0) @binding(0) var<uniform> uniforms : Uniforms;

/**
 * View-axis depth to the clip z the depth test compares.
 *
 * Nearer the eye is a larger depth, and the pipeline compares with 'less', so nearness inverts
 * on the way in. Matches the surface shader exactly, which is what lets the two share a camera.
 */
fn toClipDepth(worldDepth : f32) -> f32 {
  let extent = uniforms.params.z;
  return clamp((extent - worldDepth) / (2.0 * extent), 0.0, 1.0);
}

fn toFogAmount(worldDepth : f32) -> f32 {
  let extent = uniforms.params.z;
  let nearness = clamp((worldDepth + extent) / (2.0 * extent), 0.0, 1.0);
  return uniforms.fog.w * (1.0 - nearness);
}

/** Two lights, Blinn-Phong, against a world-space normal. */
fn toLitColor(baseColor : vec3<f32>, normal : vec3<f32>) -> vec3<f32> {
  let forward = uniforms.basisForward.xyz;
  let ambient = uniforms.params.x;
  let shininess = uniforms.params.y;

  var intensity = vec3<f32>(ambient, ambient, ambient);
  var highlight = vec3<f32>(0.0, 0.0, 0.0);

  for (var index = 0u; index < 2u; index = index + 1u) {
    let direction = uniforms.lightDirection[index].xyz;
    let diffuse = uniforms.lightDirection[index].w;
    let lightColor = uniforms.lightColor[index].rgb;
    let specular = uniforms.lightColor[index].w;

    let lambert = max(0.0, dot(normal, direction));
    // Blinn's halfway vector: one normalize rather than a reflection, indistinguishable here.
    let halfway = normalize(direction + forward);

    intensity = intensity + diffuse * lambert * lightColor;
    highlight = highlight + specular * pow(max(0.0, dot(normal, halfway)), shininess) * lightColor;
  }

  return addHighlight(max(baseColor * intensity, vec3<f32>(0.0)), highlight);
}

// ---------------------------------------------------------------------------------------------
// Nodes: one camera-facing quad per node, shaded as a sphere.
// ---------------------------------------------------------------------------------------------

struct NodeOut {
  @builtin(position) clipPosition : vec4<f32>,
  /** Position across the billboard, -1 to 1 on each axis. */
  @location(0) offset : vec2<f32>,
  @location(1) color : vec3<f32>,
  /**
   * How much this node is being emphasised, 1 to 0.
   *
   * Mixed toward the surface color rather than applied as opacity, which is what keeps the node
   * pass fully opaque: dimming against a known background is arithmetically the same as blending
   * over it, and an opaque pass needs no sorting and can write depth without punching translucent
   * holes in the edges behind it.
   */
  @location(2) emphasis : f32,
  @location(3) centerDepth : f32,
  @location(4) radius : f32,
  /** The instance index plus one, so 0 can mean "no node" in the occlusion depth texture. */
  @location(5) @interpolate(flat) nodeId : f32,
};

struct NodeFragment {
  @location(0) color : vec4<f32>,
  @builtin(frag_depth) depth : f32,
};

@vertex
fn nodeVertex(
  @builtin(vertex_index) vertexIndex : u32,
  @builtin(instance_index) instanceIndex : u32,
  @location(0) center : vec3<f32>,
  @location(1) radius : f32,
  @location(2) color : vec3<f32>,
  @location(3) emphasis : f32,
) -> NodeOut {
  var quad = array<vec2<f32>, 6>(
    vec2<f32>(-1.0, -1.0),
    vec2<f32>(1.0, -1.0),
    vec2<f32>(-1.0, 1.0),
    vec2<f32>(-1.0, 1.0),
    vec2<f32>(1.0, -1.0),
    vec2<f32>(1.0, 1.0)
  );
  let offset = quad[vertexIndex];

  // Expanded along the camera's own axes in world space, so the quad faces the eye at every angle
  // and the radius means one thing. Expanding in clip space instead would be cheaper by one
  // transform and wrong under perspective: a near node and a far one would come out the same size.
  let world = center
    + uniforms.basisRight.xyz * (offset.x * radius)
    + uniforms.basisUp.xyz * (offset.y * radius);

  let clip = uniforms.viewProjection * vec4<f32>(world, 1.0);
  // Floored for the reason the surface shader gives: past the perspective limit an unclamped w
  // goes negative and inverts the vertex through the origin.
  let clipW = max(MINIMUM_DIVISOR, clip.w);
  let centerDepth = dot(uniforms.basisForward.xyz, center);

  var out : NodeOut;
  out.clipPosition = vec4<f32>(clip.xy, toClipDepth(centerDepth) * clipW, clipW);
  out.offset = offset;
  out.color = color;
  out.emphasis = emphasis;
  out.centerDepth = centerDepth;
  out.radius = radius;
  out.nodeId = f32(instanceIndex + 1u);
  return out;
}

@fragment
fn nodeFragment(in : NodeOut) -> NodeFragment {
  let radial = length(in.offset);
  // How far \`radial\` moves across one pixel. Taken before the discard, where every invocation in
  // the quad is still running, so the derivative is defined.
  let pixel = fwidth(radial);
  if (radial > 1.0) {
    discard;
  }

  // The height of a unit hemisphere over this point of the quad, which is the impostor: a sphere's
  // normal is its surface position, so the quad's own coordinates give the normal directly.
  let bulge = sqrt(max(0.0, 1.0 - radial * radial));
  let normal = normalize(
    uniforms.basisRight.xyz * in.offset.x
      + uniforms.basisUp.xyz * in.offset.y
      + uniforms.basisForward.xyz * bulge
  );

  var lit = toLitColor(in.color, normal);

  // A ring in the surface color around every node, so two that overlap still read as two. Without
  // it a cluster of same-type nodes merges into one blob of that color and the count is lost.
  // About a pixel and a half wide on a large node, so it outlines rather than frames, and capped
  // at a share of the radius on a small one, which a fixed pixel width would otherwise fill.
  // Kept with ambient occlusion on: the occlusion fades out over its depth range, so two nodes
  // that overlap on screen but sit far apart in depth get no shading between them.
  let ringWidth = min(pixel, 0.06);
  let ringAmount = smoothstep(1.0 - 2.5 * ringWidth, 1.0 - ringWidth, radial);
  lit = mix(lit, uniforms.surface.rgb, ringAmount);

  lit = mix(uniforms.surface.rgb, lit, in.emphasis);

  // Depth from the front of the sphere rather than from its center, so two overlapping nodes
  // intersect the way spheres do instead of as a pair of flat discs.
  let depth = in.centerDepth + bulge * in.radius;

  var out : NodeFragment;
  out.color = vec4<f32>(mix(lit, uniforms.fog.rgb, toFogAmount(depth)), 1.0);
  out.depth = toClipDepth(depth);
  return out;
}

// ---------------------------------------------------------------------------------------------
// Ambient occlusion: a depth prepass over the nodes, then one full-screen multiply.
// ---------------------------------------------------------------------------------------------

struct NodeDepthFragment {
  /** View-axis depth of the sphere's front, the node's id, and its emphasis. */
  @location(0) depthAndId : vec4<f32>,
  @builtin(frag_depth) depth : f32,
};

@fragment
fn nodeDepthFragment(in : NodeOut) -> NodeDepthFragment {
  let radial = length(in.offset);
  if (radial > 1.0) {
    discard;
  }
  let depth = in.centerDepth + sqrt(max(0.0, 1.0 - radial * radial)) * in.radius;

  var out : NodeDepthFragment;
  out.depthAndId = vec4<f32>(depth, in.nodeId, in.emphasis, 0.0);
  out.depth = toClipDepth(depth);
  return out;
}

@group(0) @binding(3) var occlusionDepth : texture_2d<f32>;

const OCCLUSION_SAMPLES : u32 = 16u;
/** The most a pixel can darken, so a crowded node keeps enough of its color to read as its type. */
const OCCLUSION_MAX_DARKENING : f32 = 0.55;

struct OcclusionOut {
  @builtin(position) position : vec4<f32>,
};

@vertex
fn occlusionVertex(@builtin(vertex_index) vertexIndex : u32) -> OcclusionOut {
  var corners = array<vec2<f32>, 3>(
    vec2<f32>(-1.0, -1.0),
    vec2<f32>(3.0, -1.0),
    vec2<f32>(-1.0, 3.0)
  );
  var out : OcclusionOut;
  out.position = vec4<f32>(corners[vertexIndex], 0.0, 1.0);
  return out;
}

/**
 * How much nearer nodes hide this pixel, as a multiplier on its color.
 *
 * Sampled on a golden-angle disc around the pixel, rotated per pixel by interleaved gradient noise
 * so the pattern breaks up instead of banding. A sample counts where a different node is nearer
 * than this pixel's, fading out over the depth range so a node far in front only overlapping on
 * screen casts nothing. Samples from this pixel's own node are skipped, or every sphere would
 * shade its own rim. A background pixel has no depth to compare, so with the halo on it counts any
 * node within the radius, weighted toward the near ones.
 */
@fragment
fn occlusionFragment(in : OcclusionOut) -> @location(0) vec4<f32> {
  let strength = uniforms.occlusion.x;
  let size = vec2<i32>(textureDimensions(occlusionDepth));
  let pixel = vec2<i32>(in.position.xy);
  let center = textureLoad(occlusionDepth, pixel, 0).xyz;
  let isBackground = center.y == 0.0;
  if (strength <= 0.0 || (isBackground && uniforms.occlusion.y < 0.5)) {
    return vec4<f32>(1.0);
  }

  let radius = uniforms.occlusion.z;
  let range = uniforms.occlusion.w;
  let rotation = 6.2831853
    * fract(52.9829189 * fract(dot(in.position.xy, vec2<f32>(0.06711056, 0.00583715))));

  var occlusion = 0.0;
  for (var index = 0u; index < OCCLUSION_SAMPLES; index = index + 1u) {
    let along = (f32(index) + 0.5) / f32(OCCLUSION_SAMPLES);
    let angle = f32(index) * 2.39996323 + rotation;
    let offset = vec2<f32>(cos(angle), sin(angle)) * sqrt(along) * radius;
    let at = clamp(pixel + vec2<i32>(offset), vec2<i32>(0), size - vec2<i32>(1));
    let sample = textureLoad(occlusionDepth, at, 0).xyz;
    if (sample.y == 0.0 || sample.y == center.y) {
      continue;
    }
    if (isBackground) {
      occlusion = occlusion + (1.0 - along) * sample.z;
      continue;
    }
    let nearer = sample.x - center.x;
    if (nearer > 0.0) {
      occlusion = occlusion + (1.0 - smoothstep(0.0, range, nearer)) * sample.z;
    }
  }

  // Weighted by each node's emphasis, as caster and as receiver, so a node faded out of the focus
  // neither shades its surroundings nor is shaded back toward visibility.
  let receiver = select(center.z, 1.0, isBackground);
  let darkening = clamp(2.0 * occlusion / f32(OCCLUSION_SAMPLES), 0.0, 1.0)
    * OCCLUSION_MAX_DARKENING * strength * receiver;
  return vec4<f32>(vec3<f32>(1.0 - darkening), 1.0);
}

// ---------------------------------------------------------------------------------------------
// Volumes: one camera-facing quad per group region, shaded as a ball of tinted gas.
// ---------------------------------------------------------------------------------------------

/**
 * How much of the region's radius the tint is concentrated in, measured in from the rim.
 *
 * A region has to say where it ends without hiding what is inside it, and those pull opposite ways
 * for a uniform density: the thickest part of a filled ball is dead centre, which is exactly where
 * the marks and the bundle trunks are. Weighting the density toward the rim moves the mass off
 * them, and leaves the boundary as the brightest part of the region - which is the one thing the
 * wireframe cage this replaced did get right.
 *
 * Wide rather than a thin shell. At a tenth it reads as a bubble with a hard skin, which is the
 * cage again with soft edges; at 0.45 the falloff is long enough to read as gas that happens to be
 * denser at the edge.
 */
const VOLUME_RIM_THICKNESS : f32 = 0.45;

/**
 * Density in the clear middle, relative to the rim's.
 *
 * Not zero, and not as small as the stacking argument alone would make it. The packing puts a
 * group's marks *on* its boundary and derives its radius from them, so for a group of many
 * similar marks the rim band is exactly where the marks are and the only part of the region a
 * reader can actually see is the hollow middle. Cut this to a twentieth and those groups - the
 * large ones, the ones most in need of a boundary - came out with no region at all.
 *
 * Still an order below the rim, because it is paid at every level of a nest: a mark three groups
 * deep sits in three of these.
 */
const VOLUME_CORE_DENSITY : f32 = 0.1;

struct VolumeOut {
  @builtin(position) clipPosition : vec4<f32>,
  /** Position across the billboard, -1 to 1 on each axis. */
  @location(0) offset : vec2<f32>,
  @location(1) color : vec3<f32>,
  /** Optical depth through the thickest part of the region. */
  @location(2) density : f32,
  @location(3) centerDepth : f32,
  @location(4) radius : f32,
};

struct VolumeFragment {
  @location(0) color : vec4<f32>,
  @builtin(frag_depth) depth : f32,
};

@vertex
fn volumeVertex(
  @builtin(vertex_index) vertexIndex : u32,
  @location(0) center : vec3<f32>,
  @location(1) radius : f32,
  @location(2) color : vec3<f32>,
  @location(3) density : f32,
) -> VolumeOut {
  var quad = array<vec2<f32>, 6>(
    vec2<f32>(-1.0, -1.0),
    vec2<f32>(1.0, -1.0),
    vec2<f32>(-1.0, 1.0),
    vec2<f32>(-1.0, 1.0),
    vec2<f32>(1.0, -1.0),
    vec2<f32>(1.0, 1.0)
  );
  let offset = quad[vertexIndex];

  let world = center
    + uniforms.basisRight.xyz * (offset.x * radius)
    + uniforms.basisUp.xyz * (offset.y * radius);

  let clip = uniforms.viewProjection * vec4<f32>(world, 1.0);
  let clipW = max(MINIMUM_DIVISOR, clip.w);
  let centerDepth = dot(uniforms.basisForward.xyz, center);

  var out : VolumeOut;
  out.clipPosition = vec4<f32>(clip.xy, toClipDepth(centerDepth) * clipW, clipW);
  out.offset = offset;
  out.color = color;
  out.density = density;
  out.centerDepth = centerDepth;
  out.radius = radius;
  return out;
}

/**
 * The region as an integral through it, rather than as a lit surface.
 *
 * Two things fall out of that which an outline cannot give. The chord vanishes at the silhouette,
 * so the region fades to nothing exactly at its own boundary instead of ending on a line - there is
 * no edge to alias and none to mistake for data. And the depth written is the *near* surface, so
 * the whole ball is drawn wherever a reader can see into it: a mark in front of the region occludes
 * it, and every mark inside it and behind it is drawn over, which is what puts the near half of the
 * gas in front of its own contents.
 *
 * Beer-Lambert on the way out rather than a clamp. Two nested regions, or the near and far halves
 * of one, compose the way two clouds do - each takes a share of what is left rather than summing
 * past 1 - so a four-deep nest gets denser without ever going flat white.
 */
@fragment
fn volumeFragment(in : VolumeOut) -> VolumeFragment {
  let radial = length(in.offset);
  if (radial > 1.0) {
    discard;
  }

  let bulge = sqrt(max(0.0, 1.0 - radial * radial));
  // Chord through the ball at this point of the billboard, in radii. Zero at the silhouette.
  let chord = 2.0 * bulge;
  let rim = smoothstep(1.0 - VOLUME_RIM_THICKNESS, 1.0, radial);
  let opticalDepth = in.density * chord * (rim + VOLUME_CORE_DENSITY);
  let alpha = 1.0 - exp(-max(0.0, opticalDepth));

  let depth = in.centerDepth + bulge * in.radius;
  let faded = mix(in.color, uniforms.fog.rgb, toFogAmount(depth));

  var out : VolumeFragment;
  // Premultiplied, matching the canvas alpha mode and both sets of blend factors.
  out.color = vec4<f32>(faded * alpha, alpha);
  out.depth = toClipDepth(depth);
  return out;
}

// ---------------------------------------------------------------------------------------------
// Arrowheads: one cone per reference, standing on the mark it arrives at.
// ---------------------------------------------------------------------------------------------

/**
 * Facets around an arrowhead.
 *
 * Ten rather than a smooth cone or a flat triangle. An arrowhead is tens of pixels across at the
 * zoom a reader reads a reference at, so the facets are below the sampling either way - and this is
 * geometry rather than an impostor, so the only cost of another facet is two more triangles out of
 * twenty on something there is one of per reference.
 */
const ARROW_SIDES : u32 = 10u;

/** How long an arrowhead is, in CSS pixels. Matches ARROW_PIXELS in packNetwork.ts. */
const ARROW_PIXELS : f32 = 12.0;

/** How wide a flat glyph is drawn, in CSS pixels, and its 2:1 source shape. */
const MARKER_PIXELS : f32 = 18.0;
const MARKER_ASPECT : f32 = 0.5;

const TAU : f32 = 6.283185307179586;

/**
 * Any unit vector square to this one.
 *
 * The reference axis is swapped near the pole rather than fixed, because a cross product with a
 * nearly parallel vector loses all its precision: an arrowhead pointing within a degree of the
 * chosen axis would come out with a frame built from rounding error and spin as the camera moved.
 */
fn toSquareAxis(axis : vec3<f32>) -> vec3<f32> {
  let reference = select(
    vec3<f32>(0.0, 0.0, 1.0),
    vec3<f32>(1.0, 0.0, 0.0),
    abs(axis.z) > 0.9
  );
  return normalize(cross(axis, reference));
}

struct ArrowOut {
  @builtin(position) clipPosition : vec4<f32>,
  @location(0) color : vec3<f32>,
  @location(1) normal : vec3<f32>,
  @location(2) depth : f32,
  @location(3) presence : f32,
  /** 1 where the glyph is drawn hollow: the surface color, rimmed where it turns away. */
  @location(4) hollow : f32,
  /** Toward the eye, for the rim. Interpolated rather than recomputed per fragment. */
  @location(5) toEye : vec3<f32>,
  /** Across the billboard for a round marker, with z as the flag that it is one. */
  @location(6) impostor : vec3<f32>,
};

const BODY_CONE : f32 = 0.0;
const BODY_SPHERE : f32 = 2.0;

/**
 * How dark a hollow glyph's rim goes, and how quickly it arrives.
 *
 * A white fill with a dark stroke means *hollow*, and in two dimensions the stroke is simply the
 * outline. There is no outline in three: what a reader sees at the edge of a solid is where its
 * surface turns away from them. So the fill is the surface color and the ink is spent at grazing
 * angles, which reads as the same thing from every direction - and is what the region pass already
 * does to say where a group ends.
 */
const HOLLOW_RIM_POWER : f32 = 2.0;

@vertex
fn arrowVertex(
  @builtin(vertex_index) vertexIndex : u32,
  @location(0) tip : vec3<f32>,
  @location(1) heading : vec3<f32>,
  // the longest this may be in world units, and its base radius as a fraction of its length
  @location(2) shape : vec2<f32>,
  @location(3) color : vec3<f32>,
  @location(4) presence : f32,
  // which solid to build, and whether it is drawn hollow
  @location(5) style : vec2<f32>,
) -> ArrowOut {
  let axis = normalize(heading);
  let flank = toSquareAxis(axis);
  let lift = cross(axis, flank);

  /* Sized against the screen, by projecting a unit of world at the tip's own depth and measuring
     what it covers. Derived rather than read off the projection, so this holds whatever the camera
     is doing - and taken at the tip rather than per vertex, so the whole cone is one size instead
     of tapering with its own perspective.

     Probed along the *camera's* right, which is square to the view by construction and so always
     projects to its full length. The cone's own flank is square to the reference instead, and a
     reference pointing at the eye has a flank that projects to nothing - which read as a world
     unit covering no pixels at all, and inflated those arrowheads to their cap. */
  let clipTip = uniforms.viewProjection * vec4<f32>(tip, 1.0);
  let clipSide = uniforms.viewProjection
    * vec4<f32>(tip + uniforms.basisRight.xyz, 1.0);
  let halfViewport = vec2<f32>(uniforms.viewport.x, uniforms.viewport.y) * 0.5;
  let covered = length(
    (clipSide.xy / max(MINIMUM_DIVISOR, clipSide.w)
      - clipTip.xy / max(MINIMUM_DIVISOR, clipTip.w)) * halfViewport
  );

  /* Capped by the share of the reference the caller allows. A marker longer than the line it sits
     on is a mark of its own rather than a direction. */
  let wanted = select(
    shape.x,
    ARROW_PIXELS * uniforms.viewport.z / covered,
    covered > 1e-6
  );
  let span = min(wanted, shape.x);
  let radius = span * shape.y;
  let base = tip - axis * span;

  var world : vec3<f32>;
  var normal : vec3<f32>;
  var sphereOffset = vec2<f32>(0.0);
  var isSphere = 0.0;

  if (style.x == BODY_CONE) {
    let sideVertices = ARROW_SIDES * 3u;
    let isCap = vertexIndex >= sideVertices;
    let local = select(vertexIndex, vertexIndex - sideVertices, isCap);
    let facet = local / 3u;
    let corner = local % 3u;

    let firstAngle = TAU * f32(facet) / f32(ARROW_SIDES);
    let secondAngle = TAU * f32(facet + 1u) / f32(ARROW_SIDES);
    let outFirst = flank * cos(firstAngle) + lift * sin(firstAngle);
    let outSecond = flank * cos(secondAngle) + lift * sin(secondAngle);

    if (isCap) {
      // Wound against the side so the disc faces back down the reference it came along.
      world = select(
        select(base + outFirst * radius, base + outSecond * radius, corner == 1u),
        base,
        corner == 0u
      );
      normal = -axis;
    } else {
      world = select(
        select(base + outFirst * radius, base + outSecond * radius, corner == 2u),
        tip,
        corner == 0u
      );
      /* One normal for the whole facet, taken at its middle. A cone's normal leans out from the
         axis by the ratio of its radius to its length - square to the slant, not to the axis - so
         a shallow arrowhead catches the light along its flank rather than only at its base. */
      let outMid = normalize(outFirst + outSecond);
      normal = normalize(outMid * span + axis * radius);
    }
  } else if (style.x == BODY_SPHERE) {
    /**
     * A billboard shaded as a sphere, the way a node is, rather than a solid of its own.
     *
     * A round marker and a diamond are two different things in the notation, and a four-sided
     * bipyramid at twelve pixels is a diamond - so building one out of the other would collapse
     * two glyphs into one. The impostor is also the better sphere: its silhouette is exact at
     * every zoom, where a tessellation of sixty vertices is visibly a polygon.
     */
    var quad = array<vec2<f32>, 6>(
      vec2<f32>(-1.0, -1.0),
      vec2<f32>(1.0, -1.0),
      vec2<f32>(-1.0, 1.0),
      vec2<f32>(-1.0, 1.0),
      vec2<f32>(1.0, -1.0),
      vec2<f32>(1.0, 1.0)
    );
    let offset = quad[min(vertexIndex, 5u)];
    /* Sat just behind the vertex and touching it, which is where the 2D disc sits. */
    let middle = tip - axis * (span * 0.5);
    world = middle
      + uniforms.basisRight.xyz * (offset.x * span * 0.5)
      + uniforms.basisUp.xyz * (offset.y * span * 0.5);
    world = select(world, middle, vertexIndex >= 6u);
    normal = axis;
    sphereOffset = offset;
    isSphere = 1.0;
  } else {
    /**
     * A bipyramid on four sides, which is a diamond turned about its own line.
     *
     * Eight triangles, so the vertices past the twenty-fourth collapse onto the tip and contribute
     * nothing: one pipeline draws a fixed count per instance, and a degenerate triangle is cheaper
     * than a second draw call. Both the filled and the hollow diamond are this shape - what
     * separates them is the fill, not the form.
     *
     */
    let faces = 8u;
    let local = min(vertexIndex, faces * 3u - 1u);
    let isSpent = vertexIndex >= faces * 3u;
    let face = local / 3u;
    let corner = local % 3u;
    let isLower = face >= 4u;
    let around = face % 4u;

    let firstAngle = TAU * f32(around) / 4.0;
    let secondAngle = TAU * f32(around + 1u) / 4.0;
    let outFirst = flank * cos(firstAngle) + lift * sin(firstAngle);
    let outSecond = flank * cos(secondAngle) + lift * sin(secondAngle);

    /* Centred on the line's end rather than standing behind it, which is where a diamond sits in
       the 2D notation: the marker straddles the vertex instead of pointing at it. */
    let middle = tip - axis * (span * 0.5);
    let apex = select(middle - axis * (span * 0.5), tip, !isLower);
    let waist = span * 0.5;

    world = select(
      select(
        middle + outFirst * radius,
        middle + outSecond * radius,
        corner == 2u
      ),
      apex,
      corner == 0u
    );
    world = select(world, tip, isSpent);

    let outMid = normalize(outFirst + outSecond);
    let lean = select(-1.0, 1.0, !isLower);
    normal = normalize(outMid * waist + axis * (radius * lean));
  }

  let clip = uniforms.viewProjection * vec4<f32>(world, 1.0);
  let clipW = max(MINIMUM_DIVISOR, clip.w);
  let depth = dot(uniforms.basisForward.xyz, world);

  var out : ArrowOut;
  out.clipPosition = vec4<f32>(clip.xy, toClipDepth(depth) * clipW, clipW);
  out.color = color;
  out.normal = normal;
  out.depth = depth;
  out.presence = presence;
  out.hollow = style.y;
  out.toEye = -uniforms.basisForward.xyz;
  out.impostor = vec3<f32>(sphereOffset, isSphere);
  return out;
}

@fragment
fn arrowFragment(in : ArrowOut) -> @location(0) vec4<f32> {
  var normal = normalize(in.normal);

  if (in.impostor.z > 0.5) {
    let radial = length(in.impostor.xy);
    if (radial > 1.0) {
      discard;
    }
    // A sphere's normal is its surface position, so the billboard's own coordinates give it.
    let bulge = sqrt(max(0.0, 1.0 - radial * radial));
    normal = normalize(
      uniforms.basisRight.xyz * in.impostor.x
        + uniforms.basisUp.xyz * in.impostor.y
        + uniforms.basisForward.xyz * bulge
    );
  }

  /* The surface color where it faces the reader and the ink where it turns away, which is what a
     white fill with a dark stroke means once there is no outline to draw. */
  let grazing = 1.0 - abs(dot(normal, normalize(in.toEye)));
  let hollowColor = mix(
    uniforms.surface.rgb,
    in.color,
    pow(clamp(grazing, 0.0, 1.0), HOLLOW_RIM_POWER)
  );

  var lit = toLitColor(mix(in.color, hollowColor, in.hollow), normal);

  // Mixed toward the surface by the reference's own opacity rather than taking it as alpha, which
  // is what the node pass does with dimming and for the same reason: the pass stays opaque, so it
  // needs no sort and the depth it writes is honest for the lines that follow. An arrowhead drawn
  // solid over a reference at a third of an opacity would also be the heavier of the two marks.
  lit = mix(uniforms.surface.rgb, lit, in.presence);

  return vec4<f32>(mix(lit, uniforms.fog.rgb, toFogAmount(in.depth)), 1.0);
}

// ---------------------------------------------------------------------------------------------
// Planar markers: the 2D glyphs, on a quad that lies along the reference and faces the camera.
// ---------------------------------------------------------------------------------------------

/**
 * Sampled from the atlas rather than drawn.
 *
 * A crow's foot is strokes in a plane, with no interior and no roll angle about the reference to
 * choose - turn one ninety degrees about its own line and it is a single stroke. So this is
 * notation rather than an object, and the honest way to put notation in a 3D scene is the way this
 * renderer already puts labels there: on a quad that faces the reader.
 *
 * Oriented along the reference in *screen* space, like the ribbon it sits on, so the glyph reads
 * the way it does in every other view however the camera is turned. The atlas is black and white
 * and the luminance picks between the theme's ink and its surface, so one strip serves both
 * themes - and a white-filled disc inside a glyph comes out as the surface rather than as a hole.
 */
@group(0) @binding(1) var markerSampler : sampler;
@group(0) @binding(2) var markerAtlas : texture_2d<f32>;

/** Cells across the strip, read off the glyph list rather than restated. */
const MARKER_CELLS : f32 = ${NETWORK_MARKER_GLYPHS.length}.0;

struct PlanarOut {
  @builtin(position) clipPosition : vec4<f32>,
  @location(0) uv : vec2<f32>,
  @location(1) depth : f32,
  @location(2) presence : f32,
  @location(3) color : vec3<f32>,
};

@vertex
fn planarMarkerVertex(
  @builtin(vertex_index) vertexIndex : u32,
  @location(0) tip : vec3<f32>,
  @location(1) heading : vec3<f32>,
  // the longest this may be in world units, and which cell of the atlas it samples
  @location(2) placement : vec2<f32>,
  @location(3) presence : f32,
  @location(4) color : vec3<f32>,
) -> PlanarOut {
  var quad = array<vec2<f32>, 6>(
    vec2<f32>(0.0, 0.0),
    vec2<f32>(1.0, 0.0),
    vec2<f32>(0.0, 1.0),
    vec2<f32>(0.0, 1.0),
    vec2<f32>(1.0, 0.0),
    vec2<f32>(1.0, 1.0)
  );
  let corner = quad[vertexIndex];

  let clipTip = uniforms.viewProjection * vec4<f32>(tip, 1.0);
  let clipBack = uniforms.viewProjection * vec4<f32>(tip - heading, 1.0);
  let tipW = max(MINIMUM_DIVISOR, clipTip.w);
  let backW = max(MINIMUM_DIVISOR, clipBack.w);
  let halfViewport = vec2<f32>(uniforms.viewport.x, uniforms.viewport.y) * 0.5;

  let pixelTip = (clipTip.xy / tipW) * halfViewport;
  let pixelBack = (clipBack.xy / backW) * halfViewport;

  /* The reference's direction on screen. A reference pointing straight at the eye projects to
     nothing, and a glyph with no direction to lie along is laid flat rather than spun by rounding
     error. */
  let span = pixelTip - pixelBack;
  let spanLength = dot(span, span);
  let along = select(
    vec2<f32>(1.0, 0.0),
    span / sqrt(max(spanLength, 1e-20)),
    spanLength > 1e-12
  );
  let across = vec2<f32>(-along.y, along.x);

  /* Sized in pixels like the ribbon it belongs to, and capped by the share of the reference the
     caller allows so a short one is not swallowed by its own marker. */
  let covered = length(span);
  let worldPerPixel = select(0.0, 1.0 / covered, covered > 1e-6);
  let wanted = MARKER_PIXELS * uniforms.viewport.z;
  let capInPixels = select(wanted, placement.x / worldPerPixel, worldPerPixel > 0.0);
  let width = min(wanted, capInPixels);
  let height = width * MARKER_ASPECT;

  /* The glyph's own vertex is the right edge of its cell, so the quad hangs back from the tip. */
  let pixel = pixelTip
    - along * (width * (1.0 - corner.x))
    + across * (height * (corner.y - 0.5));

  let depth = dot(uniforms.basisForward.xyz, tip);

  var out : PlanarOut;
  out.clipPosition = vec4<f32>((pixel / halfViewport) * tipW, toClipDepth(depth) * tipW, tipW);
  out.uv = vec2<f32>((placement.y + corner.x / MARKER_CELLS), 1.0 - corner.y);
  out.depth = depth;
  out.presence = presence;
  out.color = color;
  return out;
}

@fragment
fn planarMarkerFragment(in : PlanarOut) -> @location(0) vec4<f32> {
  let sampled = textureSample(markerAtlas, markerSampler, in.uv);
  if (sampled.a < 0.02) {
    discard;
  }

  /* Luminance picks between the ink and the surface: the atlas is drawn in black and white, so a
     stroke resolves to the theme's ink and a white fill to its background. */
  let glyph = mix(uniforms.surface.rgb, in.color, 1.0 - sampled.r);
  let faded = mix(uniforms.surface.rgb, glyph, in.presence);
  let alpha = sampled.a;

  return vec4<f32>(
    mix(faded, uniforms.fog.rgb, toFogAmount(in.depth)) * alpha,
    alpha
  );
}

// ---------------------------------------------------------------------------------------------
// Lines: one screen-space ribbon per segment, for both edges and guides.
// ---------------------------------------------------------------------------------------------

struct LineOut {
  @builtin(position) clipPosition : vec4<f32>,
  @location(0) color : vec4<f32>,
  @location(1) depth : f32,
  /** Distance from the start of this line's polyline, in device pixels. */
  @location(2) dashDistance : f32,
  /** Dash and gap in device pixels. A zero dash means solid. */
  @location(3) dash : vec2<f32>,
  /**
   * How far along its route this fragment is, 0 at the source and 1 at the target.
   *
   * 1 throughout for a guide, which has no direction to carry: a grid runs between two points the
   * reader is meant to look past, so shading one end of it would be an arrow pointing at nothing -
   * and 1 is the end of the gradient that leaves it alone.
   */
  @location(4) progress : f32,
  /** Whether this route carries the travelling pulse. */
  @location(5) pulse : f32,
  /** Across the ribbon, -1 to 1 at its drawn edges. */
  @location(6) across : f32,
  /** How much of that drawn width the reference's own weight accounts for. */
  @location(7) restWidth : f32,
};

/**
 * How much the ribbon swells under the head of a pulse.
 *
 * Weight rather than brightness, because a reference is about a pixel wide and a pixel has nowhere
 * for a head and a tail to differ: the whole of a brightness pulse resolved to one faintly lighter
 * pixel, which only a difference of two frames could find. The quad is widened by this for a route
 * that pulses and masked back to the reference's own weight everywhere the pulse is not, so what a
 * reader sees is a swell running the line rather than a line that got fatter.
 */
const FLOW_PULSE_WIDTH : f32 = 2.6;

/** Device pixels between one pulse and the next. */
const FLOW_PERIOD : f32 = 190.0;

/**
 * How abruptly a pulse falls away behind its head.
 *
 * A comet rather than a band. A band of even brightness travelling a line reads as an object
 * sliding along it; a head with a tail reads as the line itself carrying something, which is the
 * thing being said - and the tail is what makes the direction legible at a glance rather than only
 * while you watch one go past.
 */
const FLOW_FALLOFF : f32 = 3.2;

@vertex
fn lineVertex(
  @builtin(vertex_index) vertexIndex : u32,
  @location(0) startPoint : vec3<f32>,
  @location(1) endPoint : vec3<f32>,
  @location(2) color : vec4<f32>,
  @location(3) width : f32,
  // distance from the polyline's start in layout units, dash on and off in CSS pixels
  @location(4) dashPattern : vec3<f32>,
  // the whole polyline's length in layout units, and whether it carries the pulse
  @location(5) flowPattern : vec2<f32>,
) -> LineOut {
  // x runs along the segment, y across it.
  var quad = array<vec2<f32>, 6>(
    vec2<f32>(0.0, -1.0),
    vec2<f32>(1.0, -1.0),
    vec2<f32>(0.0, 1.0),
    vec2<f32>(0.0, 1.0),
    vec2<f32>(1.0, -1.0),
    vec2<f32>(1.0, 1.0)
  );
  let corner = quad[vertexIndex];

  // Widened in pixels rather than in world units, which is the only way a line keeps a constant
  // weight: a world-space ribbon thins to nothing as it turns edge-on to the camera, and a graph
  // full of edges pointing in every direction would have half of them invisible.
  let clipFrom = uniforms.viewProjection * vec4<f32>(startPoint, 1.0);
  let clipTo = uniforms.viewProjection * vec4<f32>(endPoint, 1.0);
  let wFrom = max(MINIMUM_DIVISOR, clipFrom.w);
  let wTo = max(MINIMUM_DIVISOR, clipTo.w);

  let halfViewport = vec2<f32>(uniforms.viewport.x, uniforms.viewport.y) * 0.5;
  let pixelFrom = (clipFrom.xy / wFrom) * halfViewport;
  let pixelTo = (clipTo.xy / wTo) * halfViewport;

  // A segment whose endpoints project to the same pixel has no direction to widen across.
  // Guarded rather than normalized: normalize of a zero vector is NaN, and one NaN vertex takes
  // the whole triangle with it - which for a grid guide is a missing gridline at every zoom where
  // two of its points happen to coincide.
  let span = pixelTo - pixelFrom;
  let spanLength = dot(span, span);
  let direction = select(
    vec2<f32>(1.0, 0.0),
    span / sqrt(max(spanLength, 1e-20)),
    spanLength > 1e-12
  );
  let across = vec2<f32>(-direction.y, direction.x);

  let along = corner.x;
  // Room for the swell, reserved whether or not a pulse is over this fragment. The mask in the
  // fragment shader gives back everything that is not under one.
  let swell = mix(1.0, FLOW_PULSE_WIDTH, flowPattern.y);
  let halfWidth = width * uniforms.viewport.z * 0.5 * swell;
  let pixel = mix(pixelFrom, pixelTo, along) + across * (corner.y * halfWidth);

  let w = mix(wFrom, wTo, along);
  let worldDepth = dot(uniforms.basisForward.xyz, mix(startPoint, endPoint, along));

  // Dashes are measured in pixels so a dash keeps its length as the graph is zoomed, which means
  // converting the polyline's own layout units through this segment's projected scale. Per segment
  // rather than per route: a vertex shader sees one segment, and across a bundled spline's twenty
  // samples the scale varies by little enough that the phase stays continuous to the eye.
  let worldSpan = length(endPoint - startPoint);
  let pixelsPerUnit = select(
    0.0,
    length(span) / worldSpan,
    worldSpan > 1e-6
  );

  var out : LineOut;
  // Multiplied back by w because the hardware divides clip xyz by it, which returns exactly the
  // normalized device position computed above.
  out.clipPosition = vec4<f32>((pixel / halfViewport) * w, toClipDepth(worldDepth) * w, w);
  out.color = color;
  out.depth = worldDepth;
  let distanceAlong = dashPattern.x + along * worldSpan;
  out.dashDistance = distanceAlong * pixelsPerUnit;
  out.dash = dashPattern.yz * uniforms.viewport.z;
  /* A zero length is a guide, which belongs at the *full* end of the gradient rather than the
     faint one. Defaulted to zero it read as the source end of nothing, and every grid and cage in
     the view came out at half its opacity - the one thing the guide path is meant not to do. */
  out.progress = select(1.0, distanceAlong / flowPattern.x, flowPattern.x > 0.0);
  out.pulse = flowPattern.y;
  out.across = corner.y;
  out.restWidth = 1.0 / swell;
  return out;
}

@fragment
fn lineFragment(in : LineOut) -> @location(0) vec4<f32> {
  // Discarded rather than faded to zero alpha. A gap that is drawn transparent still writes nothing
  // but costs a blend, and in the additive pass a fade would accumulate where dashes cross - so the
  // gaps of a dense bundle would fill in and the whole thing would read solid again.
  let period = in.dash.x + in.dash.y;
  if (in.dash.x > 0.0 && period > 0.0) {
    let phase = in.dashDistance - period * floor(in.dashDistance / period);
    if (phase > in.dash.x) {
      discard;
    }
  }

  let fogAmount = toFogAmount(in.depth);
  let faded = mix(in.color.rgb, uniforms.fog.rgb, fogAmount);

  // A reference is drawn faintest where it leaves its source and fullest where it arrives, which
  // is the whole of what says which way it points. Static, so it is still there in a screenshot,
  // in a presentation, and for a reader who has asked for no motion.
  let lead = mix(1.0 - uniforms.flow.z, 1.0, in.progress);

  // Distance takes opacity as well as color. Fog on its own cannot thin the mass of edges at the
  // back of a graph: a line washed all the way to the surface color is still opaque, so it goes
  // on hiding whatever is behind it and the far half of the estate stays a solid sheet.
  let alpha = in.color.a * (1.0 - fogAmount * 0.4) * lead;

  // Measured in pixels travelled rather than in fraction of the route, so every pulse in the view
  // moves at one speed. Per route, a long reference would otherwise have to cover more ground in
  // the same time and would read as the busier of the two - a quantity this view does not have.
  let travelled = in.dashDistance - uniforms.flow.x;
  let cycle = travelled / FLOW_PERIOD;
  let head = pow(cycle - floor(cycle), FLOW_FALLOFF);
  let pulse = head * in.pulse * uniforms.flow.y;

  // Masked back to the reference's own weight wherever the pulse is not, which is what makes the
  // reserved width a swell rather than a thicker line. Discarded rather than faded for the reason
  // the dash gaps are: in the additive pass a transparent edge still accumulates where routes
  // cross, and a bundle of them would fill back in.
  if (abs(in.across) > mix(in.restWidth, 1.0, pulse)) {
    discard;
  }

  // Pushed away from the surface color rather than toward white, which is what makes one
  // expression serve both themes: against a dark surface a pulse brightens, and against a light
  // one - where every edge is already dark - it deepens. Toward white it would have vanished on
  // the light theme, which is the theme most readers are on.
  let lit = clamp(
    faded + (faded - uniforms.surface.rgb) * pulse * 0.9,
    vec3<f32>(0.0),
    vec3<f32>(1.0)
  );
  // Faded with distance along with everything else, so a pulse at the back of the estate does not
  // come forward of the line it is running along.
  let pulsedAlpha = min(1.0, alpha + pulse * (1.0 - fogAmount) * 0.8);

  // Premultiplied, matching the canvas alpha mode and the blend factors.
  return vec4<f32>(lit * pulsedAlpha, pulsedAlpha);
}
`;
